import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DEV-018 · Operations Project Details · Commercial Attribution card.
 *
 * Renders and edits the three commercial attribution fields:
 *   * Sales Owner (Sales-role users only, Unassigned allowed) —
 *     WRITE-gated to Operations users (`MANAGE_PROJECTS`). For Sales
 *     users this is read-only in BOTH view and edit modes.
 *   * Sales Source (canonical catalogue, Unassigned allowed) —
 *     editable by any caller with project visibility.
 *   * Sales Source Detail (optional; REQUIRED when Source = Other).
 *
 * The component is presentation + edit only — never fetches project
 * data itself; the parent `OperationsProjectDetail` owns the project
 * object and receives the updated snapshot through `onProjectUpdated`.
 *
 * Sales Owner selector shows only ACTIVE Sales-role users (via
 * `GET /api/projects/sales-owners`). If the project carries a
 * historical owner whose account is no longer eligible, the row is
 * still displayed read-only with a "historical" hint so the audit
 * trail stays coherent.
 */
const SOURCE_LABEL_FALLBACK = {
    direct_customer: "Direct Customer Enquiry",
    outbound_call: "Outbound Sales Call",
    website: "Website",
    referral: "Referral",
    existing_customer: "Existing Customer",
    social_media: "Social Media",
    event_show: "Event / Show",
    partner_introducer: "Partner / Introducer",
    other: "Other",
};

export default function ProjectAttributionSection({ project, onProjectUpdated }) {
    const { user } = useAuth();
    // DEV-018 CORRECTION · Field-level authorisation. Ownership is a
    // MANAGE_PROJECTS-gated field; every other attribution field is
    // editable by any caller with project visibility (matches the
    // backend PATCH contract). We prefer the explicit permission over
    // a role check so custom-permission Operations users behave
    // identically to the default role — falling back to the role
    // string keeps demo/dev seeds working when /auth/me doesn't emit
    // the permission array.
    const canManageProjects = useMemo(() => {
        if (!user) return false;
        if (Array.isArray(user.permissions) && user.permissions.length) {
            return user.permissions.includes("manage_projects");
        }
        return (user.role || "").toLowerCase() === "operations";
    }, [user]);
    const isSales = (user?.role || "").toLowerCase() === "sales";

    const [salesOwners, setSalesOwners] = useState([]);
    const [salesSources, setSalesSources] = useState([]);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [form, setForm] = useState({
        sales_owner_user_id: project?.sales_owner_user_id || "",
        sales_source: project?.sales_source || "",
        sales_source_detail: project?.sales_source_detail || "",
    });

    // Refresh the local form whenever a different project is selected —
    // the parent may switch projects without unmounting this card.
    useEffect(() => {
        setForm({
            sales_owner_user_id: project?.sales_owner_user_id || "",
            sales_source: project?.sales_source || "",
            sales_source_detail: project?.sales_source_detail || "",
        });
        setEditing(false);
        setErr("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    useEffect(() => {
        if (!editing) return;
        (async () => {
            try {
                // DEV-018 CORRECTION · Sales-role users cannot list
                // Sales Owners (backend requires MANAGE_PROJECTS) so
                // we skip the owner-directory fetch when it would be
                // guaranteed to 403. Source catalogue is available
                // to both roles.
                const [owners, sources] = await Promise.all([
                    canManageProjects
                        ? api.listSalesOwners().catch(() => ({ users: [] }))
                        : Promise.resolve({ users: [] }),
                    api.listSalesSources().catch(() => ({ sources: [] })),
                ]);
                setSalesOwners((owners?.users) || []);
                setSalesSources((sources?.sources) || []);
            } catch { /* non-fatal */ }
        })();
    }, [editing, canManageProjects]);

    const sourceLabel = useMemo(() => {
        const map = Object.fromEntries((salesSources || []).map((s) => [s.key, s.label]));
        return (project?.sales_source && (map[project.sales_source] || SOURCE_LABEL_FALLBACK[project.sales_source])) || null;
    }, [project?.sales_source, salesSources]);

    const owner = project?.sales_owner || null;

    const save = async () => {
        setErr("");
        if (form.sales_source === "other" && !(form.sales_source_detail || "").trim()) {
            setErr("Sales Source Detail is required when Sales Source = Other.");
            return;
        }
        setSaving(true);
        try {
            // DEV-018 CORRECTION · Only send Sales Owner when the caller
            // can actually change it. A Sales user's payload contains
            // just source + detail so the backend never returns 403
            // during the supported workflow.
            const payload = {
                sales_source: form.sales_source || null,
                sales_source_detail: (form.sales_source_detail || "").trim() || null,
            };
            if (canManageProjects) {
                payload.sales_owner_user_id = form.sales_owner_user_id || null;
            }
            const updated = await api.updateProjectAttribution(project.id, payload);
            onProjectUpdated?.(updated);
            toast.success("Sales attribution updated");
            setEditing(false);
        } catch (e) {
            setErr(formatApiError(e?.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const renderOwnerReadOnly = (
        <>
            {owner?.name || project?.sales_owner_name || (
                <span className="text-[#4B5565]">Unassigned</span>
            )}
            {owner?.resolution === "historical" && (
                <span
                    className="ml-2 text-[10px] tracking-wide uppercase text-[#B45309]"
                    data-testid="ops-project-attribution-owner-historical"
                >
                    Historical
                </span>
            )}
            {owner?.email && (
                <div className="text-xs text-[#4B5565] truncate">{owner.email}</div>
            )}
        </>
    );

    return (
        <section
            className="rounded-xl border border-[#E5E7EB] bg-white p-5"
            data-testid="ops-project-attribution"
            data-can-manage-projects={canManageProjects ? "true" : "false"}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <span aria-hidden className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#1E3A8A] shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8V4M12 8a4 4 0 100 8 4 4 0 000-8zM12 16v4"/></svg>
                    </span>
                    <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                        Sales attribution
                    </div>
                </div>
                {!editing ? (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        data-testid="ops-project-attribution-edit"
                        className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Edit
                    </button>
                ) : (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setErr(""); }}
                            data-testid="ops-project-attribution-cancel"
                            className="text-xs px-2.5 py-1 rounded-md border border-[#E5E7EB] text-[#131B2F]"
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={save}
                            data-testid="ops-project-attribution-save"
                            disabled={saving}
                            className="text-xs px-2.5 py-1 rounded-md bg-[#131B2F] text-white disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                )}
            </div>

            {!editing ? (
                <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div>
                        <dt className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium">Sales Owner</dt>
                        <dd
                            className="mt-1 text-[#131B2F]"
                            data-testid="ops-project-attribution-owner-value"
                            data-resolution={owner?.resolution || (project?.sales_owner_user_id ? "unknown" : "unassigned")}
                        >
                            {renderOwnerReadOnly}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium">Sales Source</dt>
                        <dd
                            className="mt-1 text-[#131B2F]"
                            data-testid="ops-project-attribution-source-value"
                        >
                            {sourceLabel || <span className="text-[#4B5565]">Unassigned</span>}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[10px] tracking-[0.14em] uppercase text-[#4B5565] font-medium">Source Detail</dt>
                        <dd
                            className="mt-1 text-[#131B2F]"
                            data-testid="ops-project-attribution-detail-value"
                        >
                            {project?.sales_source_detail || <span className="text-[#4B5565]">—</span>}
                        </dd>
                    </div>
                </dl>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                        <label className="block text-xs text-[#4B5565] mb-1">Sales Owner</label>
                        {canManageProjects ? (
                            <select
                                data-testid="ops-project-attribution-owner-select"
                                value={form.sales_owner_user_id}
                                onChange={(e) => setForm({ ...form, sales_owner_user_id: e.target.value })}
                                className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                            >
                                <option value="">Unassigned</option>
                                {salesOwners.map((u) => (
                                    <option key={u.id} value={u.id}
                                        data-testid={`ops-project-attribution-owner-opt-${u.id}`}>
                                        {u.name || u.email || u.id}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div
                                className="h-10 flex items-center rounded-md border border-dashed border-[#E5E7EB] bg-[#FAFAFC] px-3 text-[#131B2F]"
                                data-testid="ops-project-attribution-owner-readonly"
                                title={isSales
                                    ? "Sales Owner is managed by Operations. You can still update Sales Source and Detail."
                                    : "You do not have permission to change Sales Owner."}
                            >
                                {renderOwnerReadOnly}
                            </div>
                        )}
                        {!canManageProjects && (
                            <div
                                className="mt-1 text-[10px] text-[#4B5565]"
                                data-testid="ops-project-attribution-owner-readonly-hint"
                            >
                                Sales Owner can only be changed by Operations.
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs text-[#4B5565] mb-1">Sales Source</label>
                        <select
                            data-testid="ops-project-attribution-source-select"
                            value={form.sales_source}
                            onChange={(e) => setForm({ ...form, sales_source: e.target.value })}
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                        >
                            <option value="">Unassigned</option>
                            {(salesSources.length
                                ? salesSources
                                : Object.entries(SOURCE_LABEL_FALLBACK).map(([key, label]) => ({ key, label }))
                            ).map((s) => (
                                <option key={s.key} value={s.key}
                                    data-testid={`ops-project-attribution-source-opt-${s.key}`}>
                                    {s.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {form.sales_source && (
                        <div className="md:col-span-2">
                            <label className="block text-xs text-[#4B5565] mb-1">
                                Sales Source Detail{form.sales_source === "other" ? " (required)" : " (optional)"}
                            </label>
                            <input
                                data-testid="ops-project-attribution-detail-input"
                                value={form.sales_source_detail}
                                onChange={(e) => setForm({ ...form, sales_source_detail: e.target.value })}
                                type="text"
                                maxLength={280}
                                placeholder="Optional supporting context…"
                                className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#3E9E2E]"
                            />
                        </div>
                    )}
                    {!!err && (
                        <div className="md:col-span-2 text-xs text-[#DA3535]" data-testid="ops-project-attribution-error">
                            {err}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
